<template>
    <!-- Sidebar container with dynamic width -->
    <div class="sidebar" :style="{ width: sidebarWidth + 'px' }">
        <!-- Logo/Icon section, changes based on collapsed state -->
        <div class="logo-container">
            <div class="logo-circle" :class="{'logo-circle-ex': !collapsed}">
                <img src="/favicon.ico" :class="{'logo-ex': !collapsed, 'logo-collapsed': collapsed}" alt="icon" />
            </div>
        </div>
        <!-- Navigation links -->
        <div class="links">
            <SidebarLink to="/" icon="fa-sharp fa-home">Home</SidebarLink>
            <SidebarLink to="/hr-info" icon="fa-sharp fa-book">Employee Essentials</SidebarLink>
            <SidebarLink to="/organization" icon="fa-sharp fa-solid fa-chart-waterfall">How We Work</SidebarLink>
            <SidebarLink to="/my-department" icon="fa-sharp fa-users">My Department</SidebarLink>
        </div>
        <!-- Bottom controls: logout and collapse/expand -->
        <!-- User profile picture (above everything) -->

        <div class="sidebar-bottom">
            <!-- Move profile-container outside the flex row, above the controls -->
            <div
                class="profile-container"
                v-if="user && user.avatar_urls"
                :style="{ width: '100%', display: !collapsed ? 'flex' : 'none' }"
            >
                <a
                    :href="`/wp-admin/profile.php`"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="display: flex; justify-content: center; align-items: center; width: 100%;"
                >
                    <img
                        :src="user.avatar_urls['96'] || user.avatar_urls['48'] || user.avatar_urls['24']"
                        alt="Profile"
                        class="profile-pic"
                        :title="user.name"
                        style="display: !collapsed ? 'block' : 'none';"
                    />
                </a>
            </div>
            <div class="sidebar-controls">
                <button
                    v-if="showLogout && !collapsed"
                    class="sidebar-action-btn"
                    @click="logout"
                    title="Log out"
                >
                    <i class="fa-light fa-sign-out-alt"></i> <p>Sign Out</p>
                </button>
                <button
                    v-if="showLogout && !collapsed"
                    class="sidebar-action-btn"
                    @click="openUserSettings"
                    title="User Settings"
                >
                    <i class="fa-light fa-gear"></i> <p>User Settings</p>
                </button>
                <!-- Collapse/expand sidebar button in its own circle on the right edge -->
                <div class="collapse-circle-container" @click="toggleCollapsed">
                    <span 
                        class="collapse-icon"
                        :class="{ 'rotate-180': collapsed }"
                        tabindex="0"
                        title="Expand/collapse sidebar"
                    >
                        <i class="fa-light fa-chevrons-left"></i>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// Import SidebarLink component and sidebar state management utilities
import SidebarLink from './SidebarLink.vue';
import { collapsed, toggleCollapsed, sidebarWidth } from '../utils/state';
import { safeFetch } from '@/utils/fetch';
export default {
    name: 'Sidebar',
    props: {},
    components: { SidebarLink },
    data() {
        return {
            user: null,     // Stores current user info
            loading: false, // Loading state for user fetch
            error: null,    // Error message if user fetch fails
        };
    },
    computed: {
        // Check if user is logged in by presence of authToken
        isLoggedIn() {
            return !!localStorage.getItem('authToken');
        },
        // Show logout if not on login route and user is logged in
        showLogout() {
            return this.isLoggedIn && this.$route.path !== '/login';
        }
    },
    async mounted() {
        // Fetch user info from API if logged in, with caching
        if (this.isLoggedIn) {
            this.loading = true;
            const CACHE_KEY = 'sidebarUserCache';
            const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour

            try {
                const cached = localStorage.getItem(CACHE_KEY);
                let useCache = false;
                if (cached) {
                    const { user, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < CACHE_EXPIRY) {
                        this.user = user;
                        useCache = true;
                    }
                }
                if (!useCache) {
                    const token = localStorage.getItem('authToken');
                    const response = await safeFetch(`/wp-json/wp/v2/users/me`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        }
                    });
                    if (!response.ok) throw new Error('Failed to fetch user');
                    const user = await response.json();
                    this.user = user;
                    localStorage.setItem(CACHE_KEY, JSON.stringify({
                        user,
                        timestamp: Date.now()
                    }));
                }
            } catch (err) {
                this.error = err.message;
            } finally {
                this.loading = false;
            }
        }
    },
    methods: {
        // Logout: remove token, redirect to login, reload page
        logout() {
            localStorage.removeItem('authToken');
            localStorage.removeItem('sidebarUserCache'); // Clear cached user on logout
            this.$router.push('/login');
            location.reload();
        },
        openUserSettings() {
            window.open(`/wp-admin/profile.php`, '_blank', 'noopener');
        }
    },
    setup() {
        // Expose sidebar state and toggle function to the template
        return {
            collapsed,
            toggleCollapsed,
            sidebarWidth
        };
    },
};
</script>

<style>
:root {
    --sidebar-bg-color: #F9F8F6; /*linear-gradient(0deg,#075f92 0%, #0898d5 100%);*/
    --sidebar-text-color: #94918B;
}
</style>

<style scoped>
.sidebar {
    background: var(--sidebar-bg-color);
    color: var(--sidebar-text-color);

    float: left;
    position: fixed;
    z-index: 1;
    top: 0;
    left: 0;
    bottom: 0;

    transition: 0.3s ease;

    display: flex;
    flex-direction: column;
    align-items: center;

    text-align: center;
    padding: 5px; /* Add padding to sidebar */
}
.logo-ex {
    width: 80px;
}
.logo-collapsed {
    width: 60px;
}
.logo-ex,
.logo-collapsed {
    transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.logo-circle {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: .5em;
    background-color: #fff;
    border-radius: 100%;
    transition: 0.2s linear;
    box-sizing: border-box;
}

.logo-circle-ex {
    margin: 50px;
}
.links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: calc(100% + 5px); /* Let links reach the right edge */
    margin-right: -5px;       /* Counteract sidebar padding */
    overflow: hidden;
}
.sidebar-bottom {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    /* Remove extra horizontal padding */
    padding: 0.5em 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25em;
}
.profile-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 0.5em;
    margin-top: 0.5em;
}
.profile-pic {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
}
.sidebar-controls {
    width: 100%;
    padding: 1em 0 2em 5em;
    gap: 0.5em;
}
.sidebar-action-btn {
    background: none;
    border: none;
    color: var(--sidebar-text-color);
    font-size: 1.4em;
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
    padding: 0.75em;
    padding-top: .1em;
    padding-bottom: .5em;
    border-radius: 4px;
    transition: background 0.2s;
}
.sidebar-action-btn p {
    font-size: 80%;
    margin: 0;
}
.sidebar-action-btn:hover, .sidebar-action-btn:focus {
    background: rgba(255,255,255,0.08);
    outline: none;
}
.collapse-circle-container {
    position: absolute;
    right: -20px; /* Half of the circle's width to overlap the edge */
    bottom: 1.5em;
    width: 40px;
    height: 40px;
    background: #D5D1C8;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    cursor: pointer;
    z-index: 2;
    border: 1px solid #eee;
}
.collapse-circle-container:hover {
    background: #f0f0f0;
}
.collapse-icon {
    font-size: 14px;
    padding: 0;
    background: none;
    border: none;
    outline: none;
    transition: 0.2s linear;
    cursor: pointer;
}
.collapse-padding {
    padding: 0.75em; /*  Add right padding when not collapsed */
    padding-top: .1em;
    padding-bottom: .5em;
}
.rotate-180 {
    transform: rotate(180deg);
    transition: 0.2s linear;
}
.logout-btn {
    background: none;
    border: none;
    color: var(--sidebar-text-color);
    font-size: 1.75em;
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
    padding: 0.75em;
    padding-top: .1em;
    padding-bottom: .5em;
    border-radius: 4px;
    transition: background 0.2s;
}
.logout-btn p {
    font-size: 80%;
    margin: 0;
}
.logout-btn:hover, .logout-btn:focus {
    background: rgba(255,255,255,0.08);
    outline: none;
}
</style>
